import { useEditCanvasStore } from '../store';
import { getImageMetadata } from './getImageMetadata';
import { toNodeRect } from './layout/nodeLayout';
import { tidyNodes } from './layout/nodeTidy';

type NodeDimensions = {
  id: string;
  width: number;
  height: number;
};

/**
 * Fetches real dimensions from Imgix for nodes that were placed with fallback
 * dimensions (e.g. HEIC files the browser couldn't decode locally), resizes
 * them, and tidies the layout of all related nodes.
 *
 * Fire-and-forget — does not throw. Nodes whose metadata fetch fails stay at
 * their fallback size.
 */
export function correctNodeDimensions(
  nodesNeedingDimensions: Array<{ nodeId: string; cdnUrl: string }>,
  allNodeIds: string[],
): void {
  void Promise.allSettled(
    nodesNeedingDimensions.map(({ nodeId, cdnUrl }) =>
      getImageMetadata(cdnUrl).then(
        (imgMeta): NodeDimensions => ({
          height: imgMeta.height,
          id: nodeId,
          width: imgMeta.width,
        }),
      ),
    ),
  ).then((metaResults) => {
    const store = useEditCanvasStore.getState();
    const resizeUpdates: NodeDimensions[] = metaResults
      .filter((r): r is PromiseFulfilledResult<NodeDimensions> => r.status === 'fulfilled')
      .filter((r) => store.nodes.some((n) => n.id === r.value.id))
      .map((r) => ({ height: r.value.height, id: r.value.id, width: r.value.width }));

    if (resizeUpdates.length === 0) {
      return;
    }

    store.resizeNodes(resizeUpdates);

    // Tidy all newly-added nodes to reflow layout with corrected dimensions
    const nodeIdSet = new Set(allNodeIds);
    const freshNodes = useEditCanvasStore.getState().nodes.filter((n) => nodeIdSet.has(n.id));

    if (freshNodes.length >= 2) {
      const rects = freshNodes.map(toNodeRect);
      const newPositions = tidyNodes(rects);

      store.moveNodes(newPositions);
    }
  });
}
